import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Container,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ASSETS } from '../../../const/assets';
import { useLogin } from '../../../hooks/useAuthencation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(32, 'Password must not exceed 32 characters')
    .matches(/[A-Z]/, 'At least one uppercase letter required')
    .matches(/[a-z]/, 'At least one lowercase letter required')
    .matches(/[0-9]/, 'At least one number required')
    .matches(/[\W_]/, 'At least one special character required')
    .required('Password is required'),
});

function Login() {
  const { mutateAsync: login, isPending } = useLogin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(
    decodeURIComponent(searchParams.get('email') || '').replace(/ /g, '+')
  );
  const [password, setPassword] = useState(
    decodeURIComponent(searchParams.get('password') || '').replace(/ /g, '+')
  );

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedPassword = localStorage.getItem('rememberedPassword');
    if (rememberedEmail && rememberedPassword) {
      setRememberMe(true);
      setEmail(rememberedEmail);
      setPassword(rememberedPassword);
    }
  }, []);

  const handleSubmit = async (values: { email: string; password: string }) => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', values.email);
      localStorage.setItem('rememberedPassword', values.password);
    } else {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedPassword');
    }
    await login(values);
    navigate('/');
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      position="relative"
      sx={{
        background: '#272727',
      }}
    >
      <img
        className="absolute top-0 left-0 w-full"
        src={ASSETS.bgLogin}
        alt="bgLogin"
      />
      <Container maxWidth="xs">
        <Card
          sx={{
            padding: '20px',
            borderRadius: '16px',
            boxShadow: 3,
            position: 'relative',
            zIndex: 10,
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <div
                className="w-10 h-10 flex justify-center items-center border-[#D8DADC] rounded-[10px] border cursor-pointer"
                onClick={() => navigate('/main-login')}
              >
                <img
                  src={ASSETS.icBack2}
                  alt="back"
                  width={9}
                  height={15}
                  className="cursor-pointer"
                />
              </div>
              <img src={ASSETS.icLogo} alt="logo" width={50} height={50} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                align="left"
                sx={{
                  fontSize: '30px',
                  fontWeight: 'semibold',
                  my: '32px',
                  color: 'black',
                }}
              >
                Sign in with Email
              </Typography>
            </Box>
            <Formik
              initialValues={{ email: email, password: password }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ handleChange, handleBlur, errors, touched }) => (
                <Form>
                  <Field
                    name="email"
                    as={TextField}
                    label="Email"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.email && errors.email}
                    error={touched.email && Boolean(errors.email)}
                    autoComplete="username"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 1000px white inset',
                        WebkitTextFillColor: 'black',
                        caretColor: 'black',
                      },
                    }}
                  />
                  <Box mb={2}>
                    <Field
                      name="password"
                      as={TextField}
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      helperText={touched.password && errors.password}
                      error={touched.password && Boolean(errors.password)}
                      autoComplete="current-password"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <div
                              onClick={() => setShowPassword(!showPassword)}
                              className="cursor-pointer"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </div>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <Box className="flex justify-between mb-2">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: 'black',
                            },
                          }}
                        />
                      }
                      label="Remember me"
                      sx={{
                        color: 'black',
                        fontSize: '14px',
                        textTransform: 'none',
                        fontWeight: 'regular',
                        padding: '0',
                        '& .MuiTypography-root': {
                          fontSize: '14px',
                        },
                      }}
                    />
                    <Button
                      variant="text"
                      onClick={() => navigate('/forgot-password')}
                      sx={{
                        color: 'black',
                        fontSize: '14px',
                        textTransform: 'none',
                        fontWeight: 'regular',
                        padding: '0',
                        justifyContent: 'flex-end',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      Forgot Password?
                    </Button>
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      borderRadius: '10px',
                      backgroundColor: 'black',
                      color: 'white',
                      height: '44px',
                      fontSize: '16px',
                      fontWeight: 'medium',
                      textTransform: 'none',
                    }}
                    disabled={isPending}
                    loading={isPending}
                  >
                    Login
                  </Button>
                  <Button
                    className="mt-2"
                    variant="text"
                    fullWidth
                    onClick={() => navigate('/register')}
                    sx={{
                      color: 'black',
                      fontSize: '14px',
                      textTransform: 'none',
                      fontWeight: 'regular',
                      padding: '0',
                      marginTop: '10px',
                    }}
                  >
                    Don't have an account? Register
                  </Button>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default Login;
